import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { doc, getDoc, setDoc, collection, getDocs, writeBatch, deleteField, collectionGroup,
         query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import './App.css';
import { COLORS } from './theme';
import { DEFAULT_RANKS, normalizeRanks } from './utils';

// --- Import Sub Components ---
import AdminEvents from './components/admin/AdminEvents';
import AdminUsers from './components/admin/AdminUsers';
import AdminAccounting from './components/admin/AdminAccounting';
import AdminShrine from './components/admin/AdminShrine';
import AdminNews from './components/admin/AdminNews';

// --- Import Settings Editors ---
import TeamProfileSettingsEditor from './components/admin/TeamProfileSettingsEditor';
import ShopSettingsEditor from './components/admin/ShopSettingsEditor';
import RankSettingsEditor from './components/admin/RankSettingsEditor';
import BelongingsSettingsEditor from './components/admin/BelongingsSettingsEditor';
import AdSettingsEditor from './components/admin/AdSettingsEditor';
import SponsorSettingsEditor from './components/admin/SponsorSettingsEditor';
import AdminInquiries from './components/admin/AdminInquiries';
import MailSettingsEditor from './components/admin/MailSettingsEditor';

// 開閉可能なセクションコンポーネント
const SettingsSection = ({ title, icon, headerColor = '#f5f5f5', children, defaultOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    return (
        <div style={{marginBottom:'20px', border:'1px solid #ddd', borderRadius:'8px', background:'white', overflow:'hidden'}}>
            <div 
                onClick={() => setIsOpen(!isOpen)} 
                style={{
                    padding:'15px', background: headerColor, cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center', fontWeight:'bold', color:'#333'
                }}
            >
                <span>{icon} {title}</span>
                <span>{isOpen ? '▲ 閉じる' : '▼ 開く'}</span>
            </div>
            {isOpen && <div style={{padding:'15px'}}>{children}</div>}
        </div>
    );
};

// ★ ゲームバランス設定用の入力部品
const BalanceGroup = ({ title, desc, children }) => (
    <div style={{marginBottom:'18px', padding:'12px', background:'#fafafa', borderRadius:'8px', border:'1px solid #eee'}}>
        <div style={{fontWeight:'bold', fontSize:'13px', marginBottom:'4px', color:'#333'}}>{title}</div>
        {desc && <div style={{fontSize:'11px', color:'#777', marginBottom:'10px', lineHeight:'1.5'}}>{desc}</div>}
        <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(150px, 1fr))', gap:'10px'}}>
            {children}
        </div>
    </div>
);

const BalanceInput = ({ label, hint, value, onChange }) => (
    <div>
        <label style={{fontSize:'11px', color:'#555', display:'block', marginBottom:'3px'}}>{label}</label>
        <input
            type="number"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={hint}
            style={{width:'100%', padding:'8px', boxSizing:'border-box', border:'1px solid #ccc', borderRadius:'4px'}}
        />
        <div style={{fontSize:'10px', color:'#aaa', marginTop:'2px'}}>{hint}</div>
    </div>
);

function Admin({ onBack, userInfo = null }) {
  // ★問い合わせ一覧。タブのバッジと一覧の両方で使うため、ここで1回だけ購読する。
  //   type=='admin' の部屋だけを updatedAt 降順で50件。609件を読むことはない。
  const [inquiryRooms, setInquiryRooms] = useState([]);
  const [inquiryLoading, setInquiryLoading] = useState(true);
  useEffect(() => {
      const q = query(
          collection(db, 'chats'),
          where('type', '==', 'admin'),
          orderBy('updatedAt', 'desc'),
          limit(50)
      );
      const unsub = onSnapshot(q, (snap) => {
          setInquiryRooms(snap.docs.map(d => ({ id: d.id, ...d.data() })));
          setInquiryLoading(false);
      }, (err) => {
          console.error('問い合わせ一覧の取得エラー:', err);
          setInquiryLoading(false);
      });
      return () => unsub();
  }, []);
  const inquiryUnread = inquiryRooms.reduce((n, r) => n + (((r.unread || {}).admin) || 0), 0);

  const [adminTab, setAdminTab] = useState('events');
  
  // --- Settings State ---
  const [appTitle, setAppTitle] = useState('Revengers FC');
  const [requireApproval, setRequireApproval] = useState(true);
  const [adminPassword, setAdminPassword] = useState('');
  const [seasonStartDate, setSeasonStartDate] = useState('');
  const [bankInfo, setBankInfo] = useState('');
  const [adminPayPayId, setAdminPayPayId] = useState(''); 

  // ★ ゲームバランス設定。
  //   Cloud Functions が settings/config から読むので、ここを変えれば
  //   再デプロイなしでコインの収支を調整できる。
  //   未設定（空）の項目は Functions 側の既定値が使われる。
  const [gameBalance, setGameBalance] = useState({
      arenaEntryFee: '', arenaWinReward: '', arenaDrawReward: '', arenaMaxPaid: '',
      trainingCost: '', trainingSuperRate: '', trainingGreatRate: '',
      gachaNormalCost: '', gachaSpecialCost: '', gachaSpecial5Cost: '',
      sellN: '', sellR: '', sellS: '', sellSR: '', sellSSR: '', sellUR: '', sellLEGEND: '',
      arenaTypeBonus: '',
      // ★追加: 試合成績の配点（リアル試合の参加・得点で貰える特訓ポイント）
      matchPtParticipation: '', matchPtGoal: '', matchPtAssist: '', matchPtMom: '',
      // ★追加: 招待ボーナス（紹介した人／された人でそれぞれ設定できる）
      inviteBonusInviter: '', inviteBonusInvitee: '',
      // レアリティごとの倍率と最低ステータス（1項目あたり）
      mulN:'', mulR:'', mulS:'', mulSR:'', mulSSR:'', mulUR:'', mulLEGEND:'',
      minN:'', minR:'', minS:'', minSR:'', minSSR:'', minUR:'', minLEGEND:''
  });
  const [adConfig, setAdConfig] = useState({ enabled: false, imageUrl: '', linkUrl: '', text: '' });
  
  const [lineNotifyToken, setLineNotifyToken] = useState('');

  const [ranks, setRanks] = useState(DEFAULT_RANKS);
  const [shopItems, setShopItems] = useState([]);
  
  // 神社設定
  const [shrineName, setShrineName] = useState('勝守神社');
  const [shrineBannerUrl, setShrineBannerUrl] = useState('');
  const [shrineBgUrl, setShrineBgUrl] = useState('');

  const [actionPresets, setActionPresets] = useState([]);
  const [belongingsMaster, setBelongingsMaster] = useState([]);

  // Profile Settings
  const [faviconUrl, setFaviconUrl] = useState('');
  const [topBannerUrl, setTopBannerUrl] = useState('');
  const [teamEmblemUrl, setTeamEmblemUrl] = useState('');
  const [teamPhotoUrl, setTeamPhotoUrl] = useState('');
  const [leagueName, setLeagueName] = useState('');
  const [operatingCompany, setOperatingCompany] = useState('');
  const [teamIntro, setTeamIntro] = useState('');
  const [establishedDate, setEstablishedDate] = useState('');
  const [homeGround, setHomeGround] = useState('');
  const [snsLinks, setSnsLinks] = useState({ web: '', x: '', instagram: '', tiktok: '', youtube: '', facebook: '', threads: '', custom: '', lineGroupId: '' });
  const [showMemberList, setShowMemberList] = useState(true);

  const [pointName, setPointName] = useState('RB Coin');
  const [pointUnit, setPointUnit] = useState('RB');

  const [users, setUsers] = useState([]);

  // --- Effects ---

  useEffect(() => {
    fetchSettings();
    fetchSecrets();
    fetchUsers();
  }, []);

  // ★ 機密設定（server_secrets/line）を読み込む。管理者しか読めない。
  const fetchSecrets = async () => {
      try {
          const snap = await getDoc(doc(db, 'server_secrets', 'line'));
          if (snap.exists()) {
              const d = snap.data();
              if (d.lineNotifyToken) setLineNotifyToken(d.lineNotifyToken);
          }
      } catch (e) {
          console.warn('server_secrets の読み込みに失敗:', e?.message || e);
      }
  };

  const fetchSettings = async () => {
      try {
        const docSnap = await getDoc(doc(db, 'settings', 'config'));
        if (docSnap.exists()) {
            const data = docSnap.data();
            setAppTitle(data.appTitle || 'Revengers FC');
            setRequireApproval(data.requireApproval !== undefined ? data.requireApproval : true);
            setAdminPassword(data.adminPassword || '');
            setSeasonStartDate(data.seasonStartDate || '');
            setBankInfo(data.bankInfo || '');
            setAdminPayPayId(data.adminPayPayId || ''); 
            setFaviconUrl(data.faviconUrl || '');
            setTopBannerUrl(data.topBannerUrl || '');
            setTeamEmblemUrl(data.teamEmblemUrl || '');
            setTeamPhotoUrl(data.teamPhotoUrl || '');
            setLeagueName(data.leagueName || '');
            setOperatingCompany(data.operatingCompany || '');
            setTeamIntro(data.teamIntro || '');
            setEstablishedDate(data.establishedDate || '');
            setHomeGround(data.homeGround || '');
            setSnsLinks(data.snsLinks || { web: '', x: '', instagram: '', tiktok: '', youtube: '', facebook: '', threads: '', custom: '', lineGroupId: '' });
            setShowMemberList(data.showMemberList !== undefined ? data.showMemberList : true);
            setPointName(data.pointNameLabel || 'RB Coin'); 
            setPointUnit(data.pointUnitLabel || 'RB');
            
            setShrineName(data.shrineName || '勝守神社');
            setShrineBannerUrl(data.shrineBannerUrl || '');
            setShrineBgUrl(data.shrineBgUrl || '');

            setActionPresets(data.actionPresets || []);
            setBelongingsMaster(data.belongingsMaster || []);
            // ★ トークンは server_secrets 側を優先する（fetchSecrets で上書き）
            setLineNotifyToken(data.lineNotifyToken || '');

            // ★ ゲームバランス設定の読み込み（未設定は空文字のままにする）
            const gt = data.gachaTypes || {};
            const sp = data.cardSellPrices || {};
            const gr = data.gachaRarities || {};
            const nz = (v) => (v === undefined || v === null) ? '' : String(v);
            setGameBalance({
                arenaEntryFee: nz(data.arenaEntryFee),
                arenaWinReward: nz(data.arenaWinReward),
                arenaDrawReward: nz(data.arenaDrawReward),
                arenaMaxPaid: nz(data.arenaMaxPaid),
                trainingCost: nz(data.trainingCost),
                trainingSuperRate: nz(data.trainingSuperRate),
                trainingGreatRate: nz(data.trainingGreatRate),
                gachaNormalCost: nz(gt.normal?.cost),
                gachaSpecialCost: nz(gt.special?.cost),
                gachaSpecial5Cost: nz(gt.special5?.cost),
                sellN: nz(sp.N), sellR: nz(sp.R), sellS: nz(sp.S),
                sellSR: nz(sp.SR), sellSSR: nz(sp.SSR),
                sellUR: nz(sp.UR), sellLEGEND: nz(sp.LEGEND),
                arenaTypeBonus: nz(data.arenaTypeBonus),
                matchPtParticipation: nz(data.matchPoints?.participation),
                matchPtGoal: nz(data.matchPoints?.goal),
                matchPtAssist: nz(data.matchPoints?.assist),
                matchPtMom: nz(data.matchPoints?.mom),
                inviteBonusInviter: nz(data.inviteBonus?.inviter),
                inviteBonusInvitee: nz(data.inviteBonus?.invitee),
                mulN: nz(gr.N?.multiplier),   minN: nz(gr.N?.minStat),
                mulR: nz(gr.R?.multiplier),   minR: nz(gr.R?.minStat),
                mulS: nz(gr.S?.multiplier),   minS: nz(gr.S?.minStat),
                mulSR: nz(gr.SR?.multiplier), minSR: nz(gr.SR?.minStat),
                mulSSR: nz(gr.SSR?.multiplier), minSSR: nz(gr.SSR?.minStat),
                mulUR: nz(gr.UR?.multiplier), minUR: nz(gr.UR?.minStat),
                mulLEGEND: nz(gr.LEGEND?.multiplier), minLEGEND: nz(gr.LEGEND?.minStat)
            });
            
            if(data.adConfig) setAdConfig(data.adConfig);
            if(data.ranks && Array.isArray(data.ranks)) setRanks(data.ranks);
            if(data.shopItems && Array.isArray(data.shopItems)) setShopItems(data.shopItems);
        }
      } catch(e) {
          console.error("Settings fetch error", e);
      }
  };

  const fetchUsers = async () => {
      try {
          const snap = await getDocs(collection(db, "users"));
          const usersData = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setUsers(usersData);
      } catch(e) {
          console.error("User fetch error", e);
      }
  };

  // ★ 入力された項目だけを Firestore に送る。
  //   空欄を 0 として送ると「参加費0コイン」などの事故になるため、
  //   未入力は送らない（Functions 側の既定値が使われる）。
  const buildBalancePayload = () => {
      const out = {};
      const num = (v) => {
          if (v === '' || v === null || v === undefined) return undefined;
          const n = Number(v);
          return Number.isFinite(n) ? n : undefined;
      };
      const put = (key, v) => { const n = num(v); if (n !== undefined) out[key] = n; };

      put('arenaEntryFee', gameBalance.arenaEntryFee);
      put('arenaWinReward', gameBalance.arenaWinReward);
      put('arenaDrawReward', gameBalance.arenaDrawReward);
      put('arenaMaxPaid', gameBalance.arenaMaxPaid);
      put('trainingCost', gameBalance.trainingCost);
      put('trainingSuperRate', gameBalance.trainingSuperRate);
      put('trainingGreatRate', gameBalance.trainingGreatRate);
      put('arenaTypeBonus', gameBalance.arenaTypeBonus);

      // ★追加: 試合成績の配点。空欄の項目は送らず、Functions の既定値に任せる。
      const matchPoints = {};
      const putMp = (key, v) => { const n = num(v); if (n !== undefined) matchPoints[key] = n; };
      putMp('participation', gameBalance.matchPtParticipation);
      putMp('goal', gameBalance.matchPtGoal);
      putMp('assist', gameBalance.matchPtAssist);
      putMp('mom', gameBalance.matchPtMom);
      if (Object.keys(matchPoints).length > 0) out.matchPoints = matchPoints;

      // ★追加: 招待ボーナス
      const inviteBonus = {};
      const putIb = (key, v) => { const n = num(v); if (n !== undefined) inviteBonus[key] = n; };
      putIb('inviter', gameBalance.inviteBonusInviter);
      putIb('invitee', gameBalance.inviteBonusInvitee);
      if (Object.keys(inviteBonus).length > 0) out.inviteBonus = inviteBonus;

      // レアリティ定義（倍率・最低値）。入力された項目だけ送る。
      const rar = {};
      [['N','mulN','minN'],['R','mulR','minR'],['S','mulS','minS'],['SR','mulSR','minSR'],
       ['SSR','mulSSR','minSSR'],['UR','mulUR','minUR'],['LEGEND','mulLEGEND','minLEGEND']]
      .forEach(([k, mf, sf]) => {
          const o = {};
          const m = num(gameBalance[mf]); if (m !== undefined) o.multiplier = m;
          const mi = num(gameBalance[sf]); if (mi !== undefined) o.minStat = mi;
          if (Object.keys(o).length > 0) rar[k] = o;
      });
      if (Object.keys(rar).length > 0) out.gachaRarities = rar;

      const gachaTypes = {};
      if (num(gameBalance.gachaNormalCost) !== undefined)   gachaTypes.normal   = { cost: num(gameBalance.gachaNormalCost),   pulls: 1 };
      if (num(gameBalance.gachaSpecialCost) !== undefined)  gachaTypes.special  = { cost: num(gameBalance.gachaSpecialCost),  pulls: 1 };
      if (num(gameBalance.gachaSpecial5Cost) !== undefined) gachaTypes.special5 = { cost: num(gameBalance.gachaSpecial5Cost), pulls: 5 };
      if (Object.keys(gachaTypes).length > 0) out.gachaTypes = gachaTypes;

      const sell = {};
      [['N','sellN'],['R','sellR'],['S','sellS'],['SR','sellSR'],['SSR','sellSSR'],
       ['UR','sellUR'],['LEGEND','sellLEGEND']].forEach(([k, f]) => {
          const n = num(gameBalance[f]);
          if (n !== undefined) sell[k] = n;
      });
      if (Object.keys(sell).length > 0) out.cardSellPrices = sell;

      return out;
  };

  // ★ 既存カードのステータスを現在の設定で作り直す。
  //   確認モード（dryRun）では件数とサンプルを返すだけで書き込まない。
  const handleRecalcCards = async (dryRun) => {
      const mode = dryRun ? '【確認のみ・書き込みなし】' : '【本実行・全カードを更新します】';
      if (!window.confirm(`${mode}\n全メンバーのカードを現在の設定で再計算します。\n実行しますか？`)) return;
      try {
          const fns = getFunctions(getApp(), 'asia-northeast1');
          const res = await httpsCallable(fns, 'recalcAllCards')({ dryRun });
          const d = res?.data || {};
          console.log('=== カード再計算 ===');
          console.log(`対象ユーザー: ${d.targets} 名 / 総カード枚数: ${d.cardCount} 枚`);
          (d.samples || []).forEach(x => console.log('  ' + x));
          alert(dryRun
              ? `【確認のみ】${d.targets} 名 / ${d.cardCount} 枚が対象です。\n書き込みは行っていません。\nコンソールに変化の例を出力しました。`
              : `✅ ${d.done} 名のカードを再計算しました。`);
      } catch (e) {
          console.error(e);
          alert(e?.message || '再計算に失敗しました');
      }
  };

  const saveSettings = async () => {
      try {
          await setDoc(doc(db, 'settings', 'config'), { 
              appTitle, requireApproval, adminPassword, seasonStartDate, adConfig, bankInfo,
              adminPayPayId, faviconUrl, topBannerUrl, teamEmblemUrl, teamPhotoUrl, leagueName, operatingCompany, teamIntro, establishedDate, homeGround, snsLinks, showMemberList,
              pointNameLabel: pointName, 
              pointUnitLabel: pointUnit, 
              
              shrineName: shrineName,
              shrineBannerUrl: shrineBannerUrl,
              shrineBgUrl: shrineBgUrl,

              actionPresets: actionPresets, ranks: ranks,
              shopItems: shopItems,
              belongingsMaster: belongingsMaster,

              // ★ ゲームバランス（空欄の項目は送らず、Functions の既定値に任せる）
              ...buildBalancePayload(),

              // ★ 機密は settings/config には保存しない。
              //   settings は誰でも読めるため、ここに置くとトークンが公開されてしまう。
              //   旧データが残っていればここで削除する。
              lineNotifyToken: deleteField(),
              adminPassword: deleteField()
          }, { merge: true });

          // ★ トークンはクライアントから読めない場所へ
          await setDoc(doc(db, 'server_secrets', 'line'), {
              lineNotifyToken: lineNotifyToken || '',
              updatedAt: new Date().toISOString()
          }, { merge: true });

          alert('設定を保存しました');
      } catch(e) { alert('更新失敗'); }
  };
// ⬇️⬇️⬇️ ここから追加 ⬇️⬇️⬇️

  // ★ 全データをJSONファイルとしてダウンロードする（移行前のバックアップ用）
  //   ※ users には個人情報・パスワードが含まれます。取得後の取り扱いに注意してください。
  const handleBackupDownload = async (collectionName) => {
      if (!window.confirm(`「${collectionName}」の全データをJSONでダウンロードします。\n\n※個人情報を含む場合があります。保存先にご注意ください。`)) return;
      try {
          const snap = await getDocs(collection(db, collectionName));
          const payload = {
              collection: collectionName,
              exportedAt: new Date().toISOString(),
              count: snap.docs.length,
              docs: snap.docs.map(d => ({ id: d.id, data: d.data() }))
          };
          const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '');
          a.href = url;
          a.download = `backup_${collectionName}_${stamp}.json`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          alert(`✅ ${snap.docs.length} 件をダウンロードしました。`);
      } catch (e) {
          console.error(e);
          alert('❌ ダウンロードに失敗しました。コンソールをご確認ください。');
      }
  };

  // ★ 緊急復旧：バックアップJSONから「ランク5項目だけ」を users に書き戻す
  //   points / stats / events など他のデータには一切触れないため、
  //   復旧中に発生した購入や参加登録を巻き戻すことがない。
  const handleRestoreRanks = async (e) => {
      const file = e.target.files && e.target.files[0];
      e.target.value = '';   // 同じファイルを再選択できるようにする
      if (!file) return;

      try {
          const json = JSON.parse(await file.text());
          if (json.collection !== 'users' || !Array.isArray(json.docs)) {
              alert('❌ users のバックアップファイルではありません。');
              return;
          }

          const RANK_FIELDS = ['rankId', 'rankIds', 'membership', 'isMonthly', 'isCollaborator'];

          // どのユーザーの、どの項目が現在と違うかを先に洗い出す
          const snap = await getDocs(collection(db, 'users'));
          const current = {};
          snap.docs.forEach(d => { current[d.id] = d.data(); });

          const diffs = [];
          json.docs.forEach(item => {
              const cur = current[item.id];
              if (!cur) return;   // 消えたユーザーは対象外
              const changed = RANK_FIELDS.filter(f => JSON.stringify(cur[f]) !== JSON.stringify(item.data[f]));
              if (changed.length > 0) {
                  diffs.push({ id: item.id, name: item.data.displayName || item.id, changed, backup: item.data, cur });
              }
          });

          console.log(`===== 復旧対象 ${diffs.length} 名 / バックアップ ${json.docs.length} 名 =====`);
          console.log(`バックアップ取得日時: ${json.exportedAt}`);
          diffs.slice(0, 50).forEach(d => {
              console.log(`${d.name}:`);
              d.changed.forEach(f => console.log(`   ${f}: 現在=${JSON.stringify(d.cur[f])} → 復元=${JSON.stringify(d.backup[f])}`));
          });

          if (diffs.length === 0) {
              alert('✅ 差分はありません。復旧の必要はありません。');
              return;
          }

          if (!window.confirm(`${diffs.length} 名のランク情報を、バックアップ時点（${json.exportedAt}）の状態に戻します。\n\n対象は rankId / rankIds / membership / isMonthly / isCollaborator の5項目のみです。\nコイン・成績・参加情報には影響しません。\n\n詳細はコンソールに出力済みです。実行しますか？`)) return;

          let batch = writeBatch(db);
          let pending = 0;
          let done = 0;
          for (let i = 0; i < diffs.length; i++) {
              const d = diffs[i];
              const payload = {};
              // ★ バックアップになかった項目は deleteField() で削除し、当時の状態を正確に再現する。
              // 必ず5キー全てが入るため、payload が空になって
              // WriteBatch.update() がエラーで止まることも防げる。
              RANK_FIELDS.forEach(f => {
                  payload[f] = (d.backup[f] !== undefined) ? d.backup[f] : deleteField();
              });
              batch.update(doc(db, 'users', d.id), payload);
              pending++;
              if (pending >= 400 || i === diffs.length - 1) {
                  await batch.commit();
                  batch = writeBatch(db);
                  done += pending;
                  pending = 0;
                  console.log(`復旧進捗: ${done} / ${diffs.length}`);
              }
          }
          alert(`✅ ${done} 名のランク情報を復旧しました。\n\n続けて「公開データ一括同期」を実行して users_public も揃えてください。`);
      } catch (err) {
          console.error(err);
          alert('❌ 復旧に失敗しました。コンソールをご確認ください。');
      }
  };

  // ★ 機密フィールドを users/{id}/private/profile へ一括コピーする
  //   コピーのみで、親ドキュメントからは削除しない（第5段で別途実施）。
  //   DRY_RUN_PRIVATE = true の間は書き込みを行わず、対象の集計だけを出力する。
  const DRY_RUN_PRIVATE = true;

  const SECRET_FIELDS = ['phoneNumber', 'email', 'paypayId', 'birthDate', 'lineId', 'transferPassword'];

  const handleMigratePrivate = async () => {
      const mode = DRY_RUN_PRIVATE ? '【確認のみ・書き込みなし】' : '【本実行・書き込みあり】';
      if (!window.confirm(`${mode}\n機密情報を users/{id}/private/profile へコピーします。\n\n親ドキュメントからは削除しません。\n実行しますか？`)) return;

      try {
          const snap = await getDocs(collection(db, 'users'));
          const total = snap.docs.length;

          // すでに private を持っているユーザーを把握しておく
          const existing = {};
          try {
              const privSnap = await getDocs(collectionGroup(db, 'private'));
              privSnap.forEach(d => {
                  const uid = d.ref.parent.parent?.id;
                  if (uid) existing[uid] = d.data();
              });
          } catch (e) {
              console.warn('private の事前取得に失敗しました（初回は正常）:', e?.message || e);
          }

          // --- 事前集計 ---
          const targets = [];
          const fieldCount = {};
          SECRET_FIELDS.forEach(f => { fieldCount[f] = 0; });

          snap.docs.forEach(d => {
              const u = d.data();
              const payload = {};
              SECRET_FIELDS.forEach(f => {
                  if (u[f] !== undefined) {
                      payload[f] = u[f];
                      if (u[f] !== '' && u[f] !== null) fieldCount[f]++;
                  }
              });
              if (Object.keys(payload).length > 0) {
                  targets.push({ id: d.id, name: u.displayName || d.id, payload, hasPrivate: !!existing[d.id] });
              }
          });

          console.log(`===== 機密情報の隔離：事前チェック (全 ${total} 名) =====`);
          console.log(`コピー対象（機密を1つ以上持つ）: ${targets.length} 名`);
          console.log(`うち private が既にある: ${targets.filter(t => t.hasPrivate).length} 名`);
          console.log('--- 項目ごとの件数（空文字を除く）---');
          SECRET_FIELDS.forEach(f => console.log(`  ${f}: ${fieldCount[f]} 名`));
          console.log('--- 先頭20名 ---');
          targets.slice(0, 20).forEach(t =>
              console.log(`  ${t.name}: ${Object.keys(t.payload).join(', ')}${t.hasPrivate ? '  (private済)' : ''}`)
          );

          if (targets.length === 0) {
              alert('コピー対象がありません。');
              return;
          }

          if (DRY_RUN_PRIVATE) {
              alert(`【確認のみ】${targets.length} / ${total} 名が対象です。\n書き込みは行っていません。\nコンソールの内訳をご確認ください。`);
              return;
          }

          // --- 本実行 ---
          const timestamp = new Date().toISOString();
          let batch = writeBatch(db);
          let pending = 0;
          let done = 0;

          for (let i = 0; i < targets.length; i++) {
              const t = targets[i];
              batch.set(
                  doc(db, 'users', t.id, 'private', 'profile'),
                  { ...t.payload, migratedAt: timestamp },
                  { merge: true }
              );
              pending++;

              if (pending >= 400 || i === targets.length - 1) {
                  await batch.commit();
                  batch = writeBatch(db);
                  done += pending;
                  pending = 0;
                  console.log(`コピー進捗: ${done} / ${targets.length}`);
              }
          }

          alert(`✅ ${done} 名の機密情報を private へコピーしました。\n\n親ドキュメントのデータはそのまま残っています。\n管理画面の表示が今までどおりか確認してください。`);
      } catch (e) {
          console.error(e);
          alert('❌ 処理に失敗しました。コンソールをご確認ください。');
      }
  };

  // ★ 第7段：親ドキュメントから機密フィールドを削除する（最終工程）
  //   実行前に必ず「機密情報を private へコピー」を完了させておくこと。
  //   private にコピーが無いユーザーはスキップし、データを失わないようにする。
  const DRY_RUN_PURGE = true;

  const handlePurgeParentSecrets = async () => {
      const mode = DRY_RUN_PURGE ? '【確認のみ・削除しません】' : '【本実行・親から削除します】';
      if (!window.confirm(`${mode}\n親ドキュメント(users)から機密フィールドを削除します。\n\nprivate にコピー済みのユーザーのみが対象です。\n実行前にバックアップを取りましたか？`)) return;

      try {
          const snap = await getDocs(collection(db, 'users'));

          // private の内容を先に取得（コピー済みかどうかの判定に使う）
          const privates = {};
          const privSnap = await getDocs(collectionGroup(db, 'private'));
          privSnap.forEach(d => {
              const uid = d.ref.parent.parent?.id;
              if (uid) privates[uid] = d.data();
          });

          const targets = [];
          const skipped = [];

          snap.docs.forEach(d => {
              const u = d.data();
              const priv = privates[d.id];
              const fieldsInParent = SECRET_FIELDS.filter(f => u[f] !== undefined);
              if (fieldsInParent.length === 0) return;   // 親に機密が無い

              if (!priv) {
                  // private が無いのに親にだけある → 消すとデータが失われる
                  skipped.push(`${u.displayName || d.id}: ${fieldsInParent.join(', ')}`);
                  return;
              }
              // private 側にも存在するフィールドだけを削除対象にする
              const deletable = fieldsInParent.filter(f => priv[f] !== undefined);
              const notCopied = fieldsInParent.filter(f => priv[f] === undefined);
              if (notCopied.length > 0) {
                  skipped.push(`${u.displayName || d.id}: ${notCopied.join(', ')} が未コピー`);
              }
              if (deletable.length > 0) {
                  targets.push({ id: d.id, name: u.displayName || d.id, fields: deletable });
              }
          });

          console.log(`===== 親からの機密削除：事前チェック (全 ${snap.docs.length} 名) =====`);
          console.log(`削除対象: ${targets.length} 名`);
          console.log(`スキップ（private未コピーのため保護）: ${skipped.length} 件`);
          skipped.slice(0, 30).forEach(x => console.log('  [skip] ' + x));
          console.log('--- 削除対象の先頭20名 ---');
          targets.slice(0, 20).forEach(t => console.log(`  ${t.name}: ${t.fields.join(', ')}`));

          if (targets.length === 0) {
              alert('削除対象がありません。');
              return;
          }

          if (DRY_RUN_PURGE) {
              alert(`【確認のみ】${targets.length} 名が削除対象です。\nスキップ: ${skipped.length} 件\n\n削除は行っていません。\nコンソールをご確認ください。`);
              return;
          }

          let batch = writeBatch(db);
          let pending = 0;
          let done = 0;

          for (let i = 0; i < targets.length; i++) {
              const t = targets[i];
              const payload = {};
              t.fields.forEach(f => { payload[f] = deleteField(); });
              batch.update(doc(db, 'users', t.id), payload);
              pending++;

              if (pending >= 400 || i === targets.length - 1) {
                  await batch.commit();
                  batch = writeBatch(db);
                  done += pending;
                  pending = 0;
                  console.log(`削除進捗: ${done} / ${targets.length}`);
              }
          }

          alert(`✅ ${done} 名の親ドキュメントから機密フィールドを削除しました。\n\nこれで users を読める人でも、連絡先やパスワードは取得できません。\n管理画面とマイページの表示を確認してください。`);
      } catch (e) {
          console.error(e);
          alert('❌ 処理に失敗しました。コンソールをご確認ください。');
      }
  };

  // ★ 全ユーザーの公開データを一括同期するシステム関数
  // DRY_RUN = true の間は書き込みを行わず、コンソールに変更内容だけを出力する
  const DRY_RUN = true;

  const handleSyncPublicUsers = async () => {
      const mode = DRY_RUN ? '【確認のみ・書き込みなし】' : '【本実行・書き込みあり】';
      if (!window.confirm(`${mode}\n全メンバーの公開プロフィールを users_public へ同期します。\n実行しますか？`)) return;

      try {
          const snap = await getDocs(collection(db, "users"));
          const total = snap.docs.length;
          let count = 0;
          let batch = writeBatch(db);
          let pending = 0;

          // --- 事前チェック：rankId の型と rankIds の汚染状況 ---
          const rankIdIsArray = [];
          const dirtyRankIds = [];
          snap.docs.forEach(d => {
              const u = d.data();
              if (Array.isArray(u.rankId)) rankIdIsArray.push(u.displayName || d.id);
              if (Array.isArray(u.rankIds) && u.rankIds.length > 0) {
                  dirtyRankIds.push(`${u.displayName || d.id} | rankIds=${JSON.stringify(u.rankIds)} | membership=${u.membership} | rankId=${JSON.stringify(u.rankId)}`);
              }
          });
          console.log(`===== 事前チェック (全 ${total} 名) =====`);
          console.log(`rankId が配列のユーザー: ${rankIdIsArray.length} 名`);
          console.log(`rankIds に値が残っているユーザー: ${dirtyRankIds.length} 名`);
          dirtyRankIds.forEach(x => console.log('  ' + x));

          for (let i = 0; i < total; i++) {
              const docSnap = snap.docs[i];
              const userData = docSnap.data();
              const publicRef = doc(db, 'users_public', docSnap.id);

              // ★ rankId / rankIds の型を正規化（ともに「文字列の配列」に揃える）
              const { rankIds, rankId, membership } = normalizeRanks(userData);

              const publicData = {
                  userId: docSnap.id,
                  displayName: userData.displayName || '名無し',
                  pictureUrl: userData.pictureUrl || null,
                  bio: userData.bio || '',
                  mainPosition: userData.mainPosition || '未設定',
                  subPosition: userData.subPosition || '未設定',
                  number: userData.number || '',
                  dominantFoot: userData.dominantFoot || '右',
                  playStyle: userData.playStyle || '',
                  favoriteTeam: userData.favoriteTeam || '',
                  gender: userData.gender || '未設定',
                  // 非公開設定を考慮して安全に移行
                  height: userData.isPublicHeight !== false ? (userData.height || null) : null,
                  weight: userData.isPublicWeight === true ? (userData.weight || null) : null,
                  baseStats: userData.baseStats || { offense: 0, defense: 0, stamina: 0, speed: 0, technique: 0 },
                  stats: userData.stats || {},
                  isRetired: userData.isRetired || false,

                  // ★ 以下は App.js のランキング・チーム一覧・ガチャ・デッキが参照する必須項目
                  points: userData.points || 0,
                  totalPaid: userData.totalPaid || 0,
                  rankId: rankId,
                  rankIds: rankIds,
                  rankExpiries: userData.rankExpiries || {},
                  membership: membership,
                  role: userData.role || 'member',
                  cards: userData.cards || [],
                  deck: userData.deck || {},
                  trainingPoints: userData.trainingPoints || 0,
                  lastLogin: userData.lastLogin || null,
                  createdAt: userData.createdAt || null,
                  lastDecayDate: userData.lastDecayDate || null,

                  updatedAt: new Date().toISOString()
              };

              if (!DRY_RUN) {
                  batch.set(publicRef, publicData, { merge: true });
                  // users 本体の rankId / rankIds も同時に正規化する
                  batch.update(doc(db, 'users', docSnap.id), { rankId, rankIds, membership, isMonthly: rankIds.includes('monthly'), isCollaborator: rankIds.includes('collaborator') });
                  pending += 2;
              }
              count++;

              // Firestore のバッチ上限は 500 操作。1件につき2操作なので 400 操作ごとに区切る
              if (!DRY_RUN && (pending >= 400 || i === total - 1)) {
                  await batch.commit();
                  batch = writeBatch(db);
                  pending = 0;
                  console.log(`書き込み進捗: ${count} / ${total}`);
              }
          }

          if (DRY_RUN) {
              alert(`【確認のみ】${count} 名を検査しました。\n書き込みは行っていません。\nコンソールの結果を確認してください。`);
          } else {
              alert(`✅ 移行完了！ ${count} 名の公開データを同期しました。`);
          }
      } catch (e) {
          console.error(e);
          alert('❌ エラーが発生しました。コンソールをご確認ください。');
      }
  };

  // ==========================================================
  // ★追加: 残り振り分けポイントを「加算方式」へ移行する
  //
  // 【背景】
  //   これまで残りポイントは「100 + matchPoints + trainingPoints − baseStats合計」
  //   で毎回ゼロから計算していた。この式は過去に遡って総額を出すため、
  //   計算式を変えたり stats の読み方が変わったりすると、全員が一斉に
  //   マイナスへ振れて「特訓しても増えない」状態になっていた。
  //
  // 【この移行がすること】
  //   各メンバーの「本来の残り」を1度だけ計算し、availablePoints として保存する。
  //   マイナスの人は 0 にする（これまで振り分けた分はそのまま能力値として有効）。
  //   以降アプリは availablePoints を増減させるだけになり、過去に遡らない。
  //   これで今日を境に、誰もマイナスにならない。
  //
  //   ★ 二重実行を防ぐため、すでに availablePoints を持つ人はスキップする。
  //     （途中で失敗して再実行しても、確定済みの人を上書きしない）
  // ==========================================================
  const handleMigrateAvailablePoints = async (dryRun) => {
      const mode = dryRun ? '【確認のみ・書き込みなし】' : '【本実行・書き込みあり】';
      if (!window.confirm(`${mode}\n残り振り分けポイントを加算方式へ移行します。\nマイナスの人は 0 になります。\n実行しますか？`)) return;

      try {
          const snap = await getDocs(collection(db, "users"));
          const total = snap.docs.length;

          const sumStats = (bs) => {
              const s = bs || {};
              return (Number(s.offense)||0)+(Number(s.defense)||0)+(Number(s.stamina)||0)+(Number(s.speed)||0)+(Number(s.technique)||0);
          };

          let willSet = 0, willZero = 0, alreadyDone = 0;
          const preview = [];

          snap.docs.forEach(d => {
              const u = d.data();
              // すでに移行済みならスキップ（二重実行防止）
              if (typeof u.availablePoints === 'number') { alreadyDone++; return; }

              const base = 100;
              const match = Number(u.matchPoints) || 0;
              const training = Number(u.trainingPoints) || 0;
              const allocated = sumStats(u.baseStats);
              const raw = base + match + training - allocated;
              const available = Math.max(0, raw);

              if (raw < 0) willZero++;
              willSet++;
              if (preview.length < 30) {
                  preview.push(`${u.displayName || d.id} | 本来=${raw} → 保存=${available}${raw<0?'（0に補正）':''}`);
              }
          });

          console.log(`===== 移行プレビュー (全 ${total} 名) =====`);
          console.log(`移行対象: ${willSet} 名 / うちマイナス→0補正: ${willZero} 名 / 移行済みスキップ: ${alreadyDone} 名`);
          preview.forEach(x => console.log('  ' + x));
          if (willSet > 30) console.log(`  … 他 ${willSet - 30} 名（コンソールでは先頭30名のみ表示）`);

          if (dryRun) {
              alert(`【確認のみ】\n移行対象 ${willSet} 名（マイナス→0 が ${willZero} 名、移行済み ${alreadyDone} 名）。\n書き込みは行っていません。コンソールをご確認ください。`);
              return;
          }

          // --- 本実行 ---
          let batch = writeBatch(db);
          let pending = 0, done = 0;
          for (let i = 0; i < total; i++) {
              const d = snap.docs[i];
              const u = d.data();
              if (typeof u.availablePoints === 'number') continue;

              const allocated = sumStats(u.baseStats);
              const raw = 100 + (Number(u.matchPoints)||0) + (Number(u.trainingPoints)||0) - allocated;
              const available = Math.max(0, raw);

              batch.update(doc(db, 'users', d.id), { availablePoints: available });
              batch.set(doc(db, 'users_public', d.id), { availablePoints: available }, { merge: true });
              pending += 2; done++;

              if (pending >= 400 || i === total - 1) {
                  await batch.commit();
                  batch = writeBatch(db);
                  pending = 0;
                  console.log(`書き込み進捗: ${done} 名`);
              }
          }
          alert(`✅ 移行完了！ ${done} 名に availablePoints を設定しました。`);
      } catch (e) {
          console.error(e);
          alert('❌ エラーが発生しました。コンソールをご確認ください。');
      }
  };

  // ⬆️⬆️⬆️ ここまで追加 ⬆️⬆️⬆️
  // --- Render ---

  return (
    <div className="container" style={{maxWidth:'800px'}}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', marginTop: '20px' }}>
        <h1 style={{margin:0, fontSize:'24px'}}>🔧 管理者メニュー</h1>
        <button onClick={onBack} className="btn" style={{background:'white', border:'1px solid #ddd'}}>← アプリに戻る</button>
      </div>
      
      {/* ★変更: 'shrine' タブを削除し、5つのタブでスッキリさせる */}
      <div style={{display:'flex', gap:'10px', marginBottom:'20px', overflowX:'auto'}}>
          {['events', 'users', 'accounting', 'news', 'contact', 'ads'].map(t => (
              <button key={t} onClick={()=>setAdminTab(t)} style={{flex:1, padding:'12px', borderRadius:'8px', border:'none', background: adminTab===t?COLORS.primary:'#f5f5f5', color:adminTab===t?'white':'#666', fontWeight:'bold', fontSize:'12px', whiteSpace:'nowrap'}}>
                  {t==='events'?'📅 イベント':(t==='users'?'👥 メンバー':(t==='accounting'?'🆎 会計':(t==='news'?'📰 お知らせ':(t==='contact'?'💬 問合せ':'📢 設定'))))}
                  {t==='contact' && inquiryUnread > 0 && (
                      <span style={{marginLeft:'5px', fontSize:'10px', background:'#d32f2f', color:'white', padding:'1px 6px', borderRadius:'8px'}}>{inquiryUnread}</span>
                  )}
              </button>
          ))}
      </div>

      {/* --- Tab Contents --- */}

      {adminTab === 'events' && (
          <AdminEvents 
              ranks={ranks} 
              belongingsMaster={belongingsMaster} 
              users={users} 
          />
      )}

      {adminTab === 'users' && (
          <AdminUsers 
              ranks={ranks} 
              actionPresets={actionPresets} 
              pointUnit={pointUnit} 
          />
      )}

      {adminTab === 'accounting' && (
          <AdminAccounting 
              shopItems={shopItems} 
              pointUnit={pointUnit} 
              pointName={pointName} 
          />
      )}

      {adminTab === 'news' && (
          <AdminNews />
      )}

      {adminTab === 'contact' && (
          <AdminInquiries
              rooms={inquiryRooms}
              isLoading={inquiryLoading}
              currentUserId={userInfo?.userId || null}
              currentUserName={userInfo?.displayName || ''}
          />
      )}

      {adminTab === 'ads' && (
          <div className="card">
              
              {/* 1. 👕 チームプロフィール (基本情報) */}
              <TeamProfileSettingsEditor 
                  appTitle={appTitle} setAppTitle={setAppTitle}
                  faviconUrl={faviconUrl} setFaviconUrl={setFaviconUrl}
                  topBannerUrl={topBannerUrl} setTopBannerUrl={setTopBannerUrl}
                  teamEmblemUrl={teamEmblemUrl} setTeamEmblemUrl={setTeamEmblemUrl}
                  teamPhotoUrl={teamPhotoUrl} setTeamPhotoUrl={setTeamPhotoUrl}
                  leagueName={leagueName} setLeagueName={setLeagueName}
                  operatingCompany={operatingCompany} setOperatingCompany={setOperatingCompany}
                  teamIntro={teamIntro} setTeamIntro={setTeamIntro}
                  establishedDate={establishedDate} setEstablishedDate={setEstablishedDate}
                  homeGround={homeGround} setHomeGround={setHomeGround}
                  snsLinks={snsLinks} setSnsLinks={setSnsLinks}
                  requireApproval={requireApproval} setRequireApproval={setRequireApproval}
                  showMemberList={showMemberList} setShowMemberList={setShowMemberList}
                  lineNotifyToken={lineNotifyToken} setLineNotifyToken={setLineNotifyToken}
              />

              {/* 2. 👥 マスターデータ (ランク・持ち物) */}
              <RankSettingsEditor ranks={ranks} setRanks={setRanks} />
              
              <BelongingsSettingsEditor belongingsMaster={belongingsMaster} setBelongingsMaster={setBelongingsMaster} />

              {/* 3. 💰 会計・ショップ (商品・通貨・口座) */}
              <ShopSettingsEditor shopItems={shopItems} setShopItems={setShopItems} ranks={ranks} />

              {/* お金周りの設定をまとめるセクション */}
              <SettingsSection title="通貨・会計・口座設定" icon="💰" headerColor="#fff8e1">
                  <div style={{marginBottom:'15px', padding:'10px', background:'#f9fbe7', borderRadius:'8px', border:'1px solid #dce775'}}>
                      <h4 style={{margin:'0 0 10px 0', fontSize:'14px', color:'#827717'}}>🪙 アプリ内通貨 (ポイント)</h4>
                      <div style={{display:'flex', gap:'10px'}}>
                          <div style={{flex:2}}>
                              <label style={{fontSize:'12px', fontWeight:'bold'}}>名称</label>
                              <input value={pointName} onChange={e=>setPointName(e.target.value)} style={{width:'100%', padding:'8px', boxSizing:'border-box'}} />
                          </div>
                          <div style={{flex:1}}>
                              <label style={{fontSize:'12px', fontWeight:'bold'}}>単位</label>
                              <input value={pointUnit} onChange={e=>setPointUnit(e.target.value)} style={{width:'100%', padding:'8px', boxSizing:'border-box'}} />
                          </div>
                      </div>
                  </div>

                  <div style={{marginBottom:'10px'}}>
                      <label style={{fontSize:'12px', fontWeight:'bold', display:'block', marginBottom:'5px'}}>銀行口座情報 (振込先)</label>
                      <textarea value={bankInfo} onChange={e=>setBankInfo(e.target.value)} rows="3" style={{width:'100%', padding:'8px', boxSizing:'border-box'}} />
                  </div>
                  
                  <div style={{marginBottom:'10px'}}>
                      <label style={{fontSize:'12px', fontWeight:'bold', display:'block', marginBottom:'5px'}}>管理者PayPay ID (送金先)</label>
                      <input value={adminPayPayId} onChange={e=>setAdminPayPayId(e.target.value)} style={{width:'100%', padding:'8px', boxSizing:'border-box'}} />
                  </div>
              </SettingsSection>

              {/* 4. 📢 広告・アフィリエイト */}
              <AdSettingsEditor adConfig={adConfig} setAdConfig={setAdConfig} />

              {/* ★追加: スポンサー・提携パートナー */}
              {/*   ※ settings/sponsors に別保存するため、下の「設定をすべて保存」とは連動しない。
                    saveSettings は state の全項目を一括で書くため、同じドキュメントに入れると
                    期限切れ処理（Functions）の書き換えを巻き戻してしまう。*/}
              <SponsorSettingsEditor />

              {/* ★問い合わせのメール通知先。server_secrets/mail に保存する。
                  settings/config に置くと未ログインでも読めてしまうため。 */}
              <MailSettingsEditor />

              {/* 5. 🔑 その他システム */}
              <SettingsSection title="ゲームバランス設定" icon="🎮" headerColor="#ede7f6">
                  <div style={{fontSize:'12px', color:'#5e35b1', background:'#f3e5f5', padding:'12px', borderRadius:'8px', marginBottom:'15px', lineHeight:'1.6'}}>
                      コインの発行量と消費量を調整します。<b>空欄にすると初期値が使われます。</b><br/>
                      保存すると即座に反映されます（アプリの再デプロイは不要です）。
                  </div>

                  <BalanceGroup title="🏟️ アリーナ" desc="現在このアプリで最もコインが増えている機能です。参加費より勝利報酬が高いと、回すほど増え続けます。">
                      <BalanceInput label="参加費（2回目以降）" hint="初期値 50" value={gameBalance.arenaEntryFee} onChange={v=>setGameBalance({...gameBalance, arenaEntryFee:v})} />
                      <BalanceInput label="勝利報酬" hint="初期値 100" value={gameBalance.arenaWinReward} onChange={v=>setGameBalance({...gameBalance, arenaWinReward:v})} />
                      <BalanceInput label="引き分け報酬" hint="初期値 50" value={gameBalance.arenaDrawReward} onChange={v=>setGameBalance({...gameBalance, arenaDrawReward:v})} />
                      <BalanceInput label="1日の有償対戦の上限" hint="初期値 5（回）" value={gameBalance.arenaMaxPaid} onChange={v=>setGameBalance({...gameBalance, arenaMaxPaid:v})} />
                      <BalanceInput label="タイプ相性の倍率" hint="初期値 1.15" value={gameBalance.arenaTypeBonus} onChange={v=>setGameBalance({...gameBalance, arenaTypeBonus:v})} />
                  </BalanceGroup>

                  <div style={{fontSize:'11px', color:'#1565c0', background:'#e3f2fd', padding:'10px', borderRadius:'6px', marginBottom:'18px', lineHeight:'1.6'}}>
                      <b>⚔️ タイプ相性について</b><br/>
                      チームの能力配分から自動でタイプが決まり、三すくみで有利・不利がつきます。<br/>
                      <b>攻撃型</b>(OFF+SPD) → <b>技術型</b>(TEC+STA) → <b>守備型</b>(DEF×2) → <b>攻撃型</b><br/>
                      有利な側は試合中の能力が倍率分アップします。攻撃力の一点張りが最適解にならないための仕組みです。
                  </div>

                  <BalanceGroup title="⚽ 試合成績の配点" desc="リアルの試合に参加・得点したときに貰える特訓ポイントです。試合日の翌日 03:00 に自動で確定し、経験値として永久に加算されます。ここを変更しても、すでに付与済みのポイントは変わりません（今後の試合から適用されます）。">
                      <BalanceInput label="参加" hint="初期値 2" value={gameBalance.matchPtParticipation} onChange={v=>setGameBalance({...gameBalance, matchPtParticipation:v})} />
                      <BalanceInput label="ゴール（1点につき）" hint="初期値 2" value={gameBalance.matchPtGoal} onChange={v=>setGameBalance({...gameBalance, matchPtGoal:v})} />
                      <BalanceInput label="アシスト（1本につき）" hint="初期値 1" value={gameBalance.matchPtAssist} onChange={v=>setGameBalance({...gameBalance, matchPtAssist:v})} />
                      <BalanceInput label="MOM" hint="初期値 5" value={gameBalance.matchPtMom} onChange={v=>setGameBalance({...gameBalance, matchPtMom:v})} />
                  </BalanceGroup>

                  <BalanceGroup title="🤝 招待ボーナス" desc="招待コードが承認されたときに付与されるコインです。金額は「申請された時点」の設定で確定するため、期間限定で上げて元に戻しても、期間中に申請した人はその金額を受け取れます。0 にするとその側には付与されません。">
                      <BalanceInput label="紹介した人" hint="初期値 500" value={gameBalance.inviteBonusInviter} onChange={v=>setGameBalance({...gameBalance, inviteBonusInviter:v})} />
                      <BalanceInput label="紹介された人" hint="初期値 500" value={gameBalance.inviteBonusInvitee} onChange={v=>setGameBalance({...gameBalance, inviteBonusInvitee:v})} />
                  </BalanceGroup>

                  <BalanceGroup title="🎴 ガチャ（消費コイン）" desc="コインの主な使い道です。高くしすぎると回されなくなり、売却価格との差でコインが増減します。">
                      <BalanceInput label="通常ガチャ" hint="初期値 50" value={gameBalance.gachaNormalCost} onChange={v=>setGameBalance({...gameBalance, gachaNormalCost:v})} />
                      <BalanceInput label="レアガチャ" hint="初期値 200" value={gameBalance.gachaSpecialCost} onChange={v=>setGameBalance({...gameBalance, gachaSpecialCost:v})} />
                      <BalanceInput label="5連レアガチャ" hint="初期値 1000" value={gameBalance.gachaSpecial5Cost} onChange={v=>setGameBalance({...gameBalance, gachaSpecial5Cost:v})} />
                  </BalanceGroup>

                  <BalanceGroup title="💳 カード移籍金（売却価格）" desc="高いとガチャで使ったコインが戻ってきてしまい、実質的な消費が減ります。">
                      <BalanceInput label="N" hint="初期値 10" value={gameBalance.sellN} onChange={v=>setGameBalance({...gameBalance, sellN:v})} />
                      <BalanceInput label="R" hint="初期値 30" value={gameBalance.sellR} onChange={v=>setGameBalance({...gameBalance, sellR:v})} />
                      <BalanceInput label="S" hint="初期値 50" value={gameBalance.sellS} onChange={v=>setGameBalance({...gameBalance, sellS:v})} />
                      <BalanceInput label="SR" hint="初期値 100" value={gameBalance.sellSR} onChange={v=>setGameBalance({...gameBalance, sellSR:v})} />
                      <BalanceInput label="SSR" hint="初期値 300" value={gameBalance.sellSSR} onChange={v=>setGameBalance({...gameBalance, sellSSR:v})} />
                      <BalanceInput label="UR" hint="初期値 600" value={gameBalance.sellUR} onChange={v=>setGameBalance({...gameBalance, sellUR:v})} />
                      <BalanceInput label="LEGEND" hint="初期値 1500" value={gameBalance.sellLEGEND} onChange={v=>setGameBalance({...gameBalance, sellLEGEND:v})} />
                  </BalanceGroup>

                  <BalanceGroup title="🏋️ 特訓" desc="1日1回は無料です。2回目以降の消費コインと、成功確率を設定します。">
                      <BalanceInput label="消費コイン（2回目以降）" hint="初期値 50" value={gameBalance.trainingCost} onChange={v=>setGameBalance({...gameBalance, trainingCost:v})} />
                      <BalanceInput label="超成功(+5)の確率 %" hint="初期値 5" value={gameBalance.trainingSuperRate} onChange={v=>setGameBalance({...gameBalance, trainingSuperRate:v})} />
                      <BalanceInput label="大成功(+3)の確率 %" hint="初期値 25" value={gameBalance.trainingGreatRate} onChange={v=>setGameBalance({...gameBalance, trainingGreatRate:v})} />
                  </BalanceGroup>

                  <BalanceGroup title="⭐ レアリティの強さ（倍率）" desc="カードのステータス = (本人の能力 + 唯一カードのボーナス÷5) × 倍率。低いとレアを引く価値が薄くなります。">
                      <BalanceInput label="N 倍率" hint="初期値 1.0" value={gameBalance.mulN} onChange={v=>setGameBalance({...gameBalance, mulN:v})} />
                      <BalanceInput label="R 倍率" hint="初期値 1.15" value={gameBalance.mulR} onChange={v=>setGameBalance({...gameBalance, mulR:v})} />
                      <BalanceInput label="S 倍率" hint="初期値 1.35" value={gameBalance.mulS} onChange={v=>setGameBalance({...gameBalance, mulS:v})} />
                      <BalanceInput label="SR 倍率" hint="初期値 1.6" value={gameBalance.mulSR} onChange={v=>setGameBalance({...gameBalance, mulSR:v})} />
                      <BalanceInput label="SSR 倍率" hint="初期値 2.0" value={gameBalance.mulSSR} onChange={v=>setGameBalance({...gameBalance, mulSSR:v})} />
                      <BalanceInput label="UR 倍率" hint="初期値 2.6" value={gameBalance.mulUR} onChange={v=>setGameBalance({...gameBalance, mulUR:v})} />
                      <BalanceInput label="LEGEND 倍率" hint="初期値 3.5" value={gameBalance.mulLEGEND} onChange={v=>setGameBalance({...gameBalance, mulLEGEND:v})} />
                  </BalanceGroup>

                  <BalanceGroup title="🛡️ 最低ステータス保証（1項目あたり）" desc="ステータスを振り分けていないメンバーのカードが全項目0になるのを防ぎます。現在9割以上のメンバーが未振り分けのため、必須の設定です。">
                      <BalanceInput label="N" hint="初期値 12" value={gameBalance.minN} onChange={v=>setGameBalance({...gameBalance, minN:v})} />
                      <BalanceInput label="R" hint="初期値 15" value={gameBalance.minR} onChange={v=>setGameBalance({...gameBalance, minR:v})} />
                      <BalanceInput label="S" hint="初期値 18" value={gameBalance.minS} onChange={v=>setGameBalance({...gameBalance, minS:v})} />
                      <BalanceInput label="SR" hint="初期値 24" value={gameBalance.minSR} onChange={v=>setGameBalance({...gameBalance, minSR:v})} />
                      <BalanceInput label="SSR" hint="初期値 32" value={gameBalance.minSSR} onChange={v=>setGameBalance({...gameBalance, minSSR:v})} />
                      <BalanceInput label="UR" hint="初期値 45" value={gameBalance.minUR} onChange={v=>setGameBalance({...gameBalance, minUR:v})} />
                      <BalanceInput label="LEGEND" hint="初期値 60" value={gameBalance.minLEGEND} onChange={v=>setGameBalance({...gameBalance, minLEGEND:v})} />
                  </BalanceGroup>

                  <div style={{marginBottom:'18px', padding:'15px', background:'#fff3e0', borderRadius:'8px', border:'2px solid #ffb74d'}}>
                      <div style={{fontWeight:'bold', fontSize:'13px', marginBottom:'6px', color:'#e65100'}}>🔄 既存カードのステータス再計算</div>
                      <div style={{fontSize:'11px', color:'#ef6c00', marginBottom:'10px', lineHeight:'1.6'}}>
                          倍率や最低保証を変更しても、<b>すでに配られたカードの数値は変わりません</b>。<br/>
                          このボタンで全カードを現在の設定で作り直します。<br/>
                          カードの枚数やレアリティは変わらず、<b>ステータスと見た目だけ</b>が更新されます。<br/>
                          <b style={{color:'#c62828'}}>先に「設定をすべて保存」してから実行してください。</b>
                      </div>
                      <div style={{display:'flex', gap:'10px', flexWrap:'wrap'}}>
                          <button onClick={()=>handleRecalcCards(true)} className="btn" style={{flex:1, minWidth:'140px', background:'white', border:'1px solid #e65100', color:'#e65100', fontWeight:'bold', padding:'10px'}}>
                              🔍 確認のみ（書き込みなし）
                          </button>
                          <button onClick={()=>handleRecalcCards(false)} className="btn" style={{flex:1, minWidth:'140px', background:'#e65100', color:'white', border:'none', fontWeight:'bold', padding:'10px'}}>
                              🔄 再計算を実行する
                          </button>
                      </div>
                  </div>

                  <div style={{fontSize:'11px', color:'#666', background:'#fafafa', padding:'12px', borderRadius:'8px', border:'1px solid #eee', lineHeight:'1.7'}}>
                      <b>💡 調整の目安</b><br/>
                      ・<b>コインが増えすぎている</b>とき → アリーナの勝利報酬を下げる／参加費を上げる／有償上限を減らす<br/>
                      ・<b>ガチャが回されない</b>とき → ガチャの消費コインを下げる<br/>
                      ・<b>ガチャの実質負担が軽すぎる</b>とき → カード移籍金を下げる<br/>
                      ・変更後は1週間ほど様子を見て、コイン通帳の増減で効果を確認してください。
                  </div>
              </SettingsSection>

              <SettingsSection title="その他・システム設定" icon="🔑">
                  <div style={{marginBottom:'10px'}}>
                      <label style={{fontSize:'12px', fontWeight:'bold', display:'block', marginBottom:'5px'}}>管理者パスワード (旧)</label>
                      <input value={adminPassword} onChange={e=>setAdminPassword(e.target.value)} placeholder="※現在は権限チェックが優先されます" style={{width:'100%', padding:'8px', boxSizing:'border-box'}} />
                  </div>
                  {/* ★追加：バックアップ（移行前に必ず実行） */}
                  <div style={{marginTop: '20px', padding: '15px', background: '#e8f5e9', borderRadius: '8px', border: '1px solid #a5d6a7'}}>
                      <h4 style={{margin: '0 0 10px 0', fontSize: '13px', color: '#2e7d32'}}>💾 バックアップ（先に実行してください）</h4>
                      <p style={{fontSize: '11px', color: '#388e3c', marginBottom: '10px'}}>
                          現在のデータをJSONファイルとして端末にダウンロードします。<br/>
                          <b style={{color:'#c62828'}}>※ users には個人情報が含まれます。保存先と削除の管理にご注意ください。</b>
                      </p>
                      <div style={{display:'flex', gap:'8px', flexWrap:'wrap'}}>
                          {['users', 'users_public', 'events', 'point_logs', 'accounting'].map(c => (
                              <button key={c} onClick={() => handleBackupDownload(c)} className="btn" style={{background:'white', border:'1px solid #66bb6a', color:'#2e7d32', fontSize:'11px', padding:'8px 10px', fontWeight:'bold'}}>
                                  ⬇️ {c}
                              </button>
                          ))}
                      </div>
                  </div>

                  {/* ★追加：緊急復旧 */}
                  <div style={{marginTop: '15px', padding: '15px', background: '#fff3e0', borderRadius: '8px', border: '1px solid #ffcc80'}}>
                      <h4 style={{margin: '0 0 10px 0', fontSize: '13px', color: '#e65100'}}>🛟 緊急復旧（ランク情報のみ）</h4>
                      <p style={{fontSize: '11px', color: '#ef6c00', marginBottom: '10px', lineHeight: '1.5'}}>
                          バックアップした <b>users</b> のJSONを選ぶと、<br/>
                          rankId / rankIds / membership / isMonthly / isCollaborator の5項目だけを書き戻します。<br/>
                          <b style={{color:'#2e7d32'}}>コイン・成績・参加情報には影響しません。</b><br/>
                          差分はコンソールに表示され、確認してから実行できます。
                      </p>
                      <input type="file" accept="application/json,.json" onChange={handleRestoreRanks} style={{fontSize:'11px'}} />
                  </div>

                  {/* ★追加：機密情報の隔離 */}
                  <div style={{marginTop: '15px', padding: '15px', background: '#ede7f6', borderRadius: '8px', border: '1px solid #b39ddb'}}>
                      <h4 style={{margin: '0 0 10px 0', fontSize: '13px', color: '#4527a0'}}>🔒 機密情報の隔離（private へコピー）</h4>
                      <p style={{fontSize: '11px', color: '#5e35b1', marginBottom: '10px', lineHeight: '1.5'}}>
                          電話番号・メール・PayPay ID・生年月日・LINE ID・パスワードを<br/>
                          <b>users/(ID)/private/profile</b> へコピーします。<br/>
                          <b style={{color:'#2e7d32'}}>親ドキュメントからは削除しません（今までどおり表示されます）。</b>
                      </p>
                      <button onClick={handleMigratePrivate} className="btn" style={{width:'100%', background:'#5e35b1', color:'white', fontWeight:'bold', padding:'12px', border:'none'}}>
                          🔒 機密情報を private へコピーする
                      </button>
                  </div>

                  {/* ★追加：親から機密を削除（最終工程） */}
                  <div style={{marginTop: '15px', padding: '15px', background: '#ffebee', borderRadius: '8px', border: '2px solid #ef9a9a'}}>
                      <h4 style={{margin: '0 0 10px 0', fontSize: '13px', color: '#c62828'}}>🧹 親ドキュメントから機密を削除（最終工程）</h4>
                      <p style={{fontSize: '11px', color: '#d32f2f', marginBottom: '10px', lineHeight: '1.5'}}>
                          users 本体から電話番号・メール・PayPay ID・生年月日・LINE ID・パスワードを削除します。<br/>
                          <b>private にコピー済みのユーザーのみが対象です。</b>未コピーのものは保護されます。<br/>
                          <b style={{color:'#b71c1c'}}>実行前に必ずバックアップを取得してください。</b>
                      </p>
                      <button onClick={handlePurgeParentSecrets} className="btn" style={{width:'100%', background:'#c62828', color:'white', fontWeight:'bold', padding:'12px', border:'none'}}>
                          🧹 親から機密フィールドを削除する
                      </button>
                  </div>

                  {/* ★追加：公開データ一括同期ボタン */}
                  <div style={{marginTop: '20px', padding: '15px', background: '#ffebee', borderRadius: '8px', border: '1px solid #ffcdd2'}}>
                      <h4 style={{margin: '0 0 10px 0', fontSize: '13px', color: '#c62828'}}>⚠️ データベース保守</h4>
                      <p style={{fontSize: '11px', color: '#d32f2f', marginBottom: '10px'}}>
                          全メンバーの安全な公開用プロフィールデータを生成・同期します。セキュリティ強化のために一度だけ実行してください。
                      </p>
                      <button onClick={handleSyncPublicUsers} className="btn" style={{background: '#d32f2f', color: 'white', fontWeight: 'bold', width: '100%', padding: '10px'}}>
                          🚀 全ユーザーの公開データを一括同期する
                      </button>

                      {/* ★追加：残り振り分けポイントの加算方式への移行 */}
                      <p style={{fontSize: '11px', color: '#d32f2f', margin: '16px 0 10px 0'}}>
                          残り振り分けポイントを「加算方式」へ移行します。各メンバーの現在の残りを保存し、以降はマイナスにならないようにします。まず「確認のみ」で対象人数を見てから本実行してください。<b>一度だけ</b>実行します。
                      </p>
                      <div style={{display:'flex', gap:'8px'}}>
                          <button onClick={() => handleMigrateAvailablePoints(true)} className="btn" style={{background: '#607d8b', color: 'white', fontWeight: 'bold', flex: 1, padding: '10px'}}>
                              🔍 確認のみ
                          </button>
                          <button onClick={() => handleMigrateAvailablePoints(false)} className="btn" style={{background: '#c62828', color: 'white', fontWeight: 'bold', flex: 1, padding: '10px'}}>
                              ▶ 本実行
                          </button>
                      </div>
                  </div>
              </SettingsSection>

              {/* ★変更: 神社設定をここに移動（折りたたみセクションとして） */}
              <SettingsSection title="神社機能の設定" icon="⛩️" headerColor="#fff3e0">
                  <AdminShrine 
                      pointUnit={pointUnit} 
                      shrineName={shrineName}
                      setShrineName={setShrineName}
                      shrineBannerUrl={shrineBannerUrl}
                      setShrineBannerUrl={setShrineBannerUrl}
                      shrineBgUrl={shrineBgUrl}
                      setShrineBgUrl={setShrineBgUrl}
                      onSave={saveSettings} 
                  />
              </SettingsSection>

              <button onClick={saveSettings} className="btn btn-primary" style={{width:'100%', padding:'15px', marginTop:'20px'}}>設定をすべて保存</button>
          </div>
      )}
    </div>
  );
}

export default Admin;